import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RatingScale from "../components/RatingScale";
import PostAnalytics from "../components/PostAnalytics";
import { toast } from "sonner";
import { Star, ArrowLeft, Lock, Eye, Flag, Trash2 } from "lucide-react";
import ReportDialog from "../components/ReportDialog";
import { useNavigate } from "react-router-dom";

export default function PostDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [rating, setRating] = useState(null);
    const [raters, setRaters] = useState(null);
    const [ratersLocked, setRatersLocked] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([
                api.get(`/posts/${id}`),
                api.get(`/posts/${id}/comments`),
            ]);
            setPost(p.data);
            setRating(p.data.my_rating);
            setComments(c.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const rate = async (n) => {
        if (!user) { toast.error("Log in to rate"); return; }
        try {
            const { data } = await api.post(`/posts/${id}/rate`, { rating: n });
            setPost(data);
            setRating(data.my_rating);
            toast.success(`Rated ${n}/10`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        try {
            const { data } = await api.post(`/posts/${id}/comments`, { text: text.trim() });
            setComments([data, ...comments]);
            setText("");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const loadRaters = async () => {
        try {
            const { data } = await api.get(`/posts/${id}/raters`);
            setRaters(data);
            setRatersLocked(false);
        } catch (e) {
            if (e?.response?.status === 402) setRatersLocked(true);
            else toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        if (post && user && user.id === post.user_id) {
            if (user.tier && user.tier !== "free") loadRaters();
            else setRatersLocked(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post, user]);

    if (loading) return <main className="max-w-2xl mx-auto p-8 font-bold text-[#4A4A4A]">Loading…</main>;
    if (!post) return <main className="max-w-2xl mx-auto p-8">Not found</main>;

    const isOwn = user && user.id === post.user_id;

    return (
        <main className="max-w-2xl mx-auto px-4 py-6">
            <Link to="/" className="inline-flex items-center gap-1 mb-4 text-sm font-bold hover:underline" data-testid="back-link">
                <ArrowLeft className="w-4 h-4" /> Back to feed
            </Link>
            {user && !isOwn && (
                <button
                    onClick={() => setReportOpen(true)}
                    className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-[#4A4A4A] hover:text-[#FF5A5F]"
                    data-testid="detail-report-btn"
                >
                    <Flag className="w-3.5 h-3.5" /> Report
                </button>
            )}
            {isOwn && (
                <button
                    onClick={async () => {
                        if (!window.confirm("Delete this post? This cannot be undone.")) return;
                        try {
                            await api.delete(`/posts/${post.id}`);
                            toast.success("Post deleted");
                            navigate(`/u/${user.username}`);
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-[#4A4A4A] hover:text-[#FF5A5F]"
                    data-testid="detail-delete-btn"
                >
                    <Trash2 className="w-3.5 h-3.5" /> Delete post
                </button>
            )}
            {reportOpen && <ReportDialog postId={post.id} onClose={() => setReportOpen(false)} />}

            <article className="bg-white brut-static rounded-xl overflow-hidden" data-testid={`post-detail-${post.id}`}>
                <header className="flex items-center justify-between px-4 py-3 border-b-2 border-[#0A0A0A]">
                    <Link to={`/u/${post.username}`} className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-[#B8E1FF] border-2 border-[#0A0A0A] flex items-center justify-center font-black">
                            {post.username?.[0]?.toUpperCase()}
                        </div>
                        <div>
                            <div className="font-bold">@{post.username}</div>
                            <div className="text-xs text-[#4A4A4A]">{new Date(post.created_at).toLocaleString()}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1 bg-[#FFE55C] px-3 py-1 rounded-full border-2 border-[#0A0A0A]">
                        <Star className="w-3.5 h-3.5 fill-[#0A0A0A]" />
                        <span className="font-black text-sm">{post.avg_rating.toFixed(1)}</span>
                        <span className="text-xs font-medium">({post.rating_count})</span>
                    </div>
                </header>

                <img
                    src={fileUrl(post.image_path)}
                    alt={post.caption}
                    className="w-full max-h-[70vh] object-cover border-b-2 border-[#0A0A0A]"
                />

                {post.caption && <div className="px-4 py-3 text-sm font-medium">{post.caption}</div>}

                <div className="px-4 py-4 border-t-2 border-[#0A0A0A]">
                    {isOwn ? (
                        <div className="text-sm text-[#4A4A4A] italic font-medium">You can&apos;t rate your own post.</div>
                    ) : (
                        <>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">
                                {rating ? `You rated ${rating}/10 — tap to change` : "Rate this 1 to 10"}
                            </div>
                            <RatingScale value={rating} onChange={rate} testIdPrefix={`detail-rating-${post.id}`} />
                        </>
                    )}
                </div>
            </article>

            {isOwn && (
                <section className="mt-6 bg-white brut-static rounded-xl p-5" data-testid="who-rated-you-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-5 h-5" />
                        <h3 className="display text-xl font-black">Who rated you</h3>
                    </div>
                    {ratersLocked ? (
                        <div className="rounded-lg bg-[#FFE55C] border-2 border-[#0A0A0A] p-4 flex items-start gap-3">
                            <Lock className="w-5 h-5 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-black">Premium feature</div>
                                <div className="text-sm font-medium text-[#0A0A0A]/80">
                                    See every rater and their exact score. Included with Pod Member ($4.99/mo).
                                </div>
                            </div>
                            <Link to="/pricing" className="px-3 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold text-sm" data-testid="raters-upgrade-cta">
                                Upgrade
                            </Link>
                        </div>
                    ) : raters === null ? (
                        <div className="text-sm text-[#4A4A4A] font-medium">Loading…</div>
                    ) : raters.length === 0 ? (
                        <div className="text-sm text-[#4A4A4A] font-medium italic">No ratings yet — share your post!</div>
                    ) : (
                        <ul className="space-y-2">
                            {raters.map((r) => (
                                <li key={`${r.username}-${r.at}`} className="flex items-center justify-between text-sm font-medium" data-testid={`rater-${r.username}`}>
                                    <Link to={`/u/${r.username}`} className="hover:underline">@{r.username}</Link>
                                    <span className="font-black">{r.rating}/10</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {isOwn && <PostAnalytics postId={post.id} />}

            <section className="mt-8" data-testid="comments-section">
                <h2 className="display text-2xl font-black mb-4">Comments <span className="text-[#4A4A4A] text-lg">({comments.length})</span></h2>

                {user ? (
                    <form onSubmit={submitComment} className="flex gap-2 mb-6" data-testid="comment-form">
                        <input
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            maxLength={500}
                            placeholder="Say something nice…"
                            className="flex-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                            data-testid="comment-input"
                        />
                        <button type="submit" className="px-4 py-3 rounded-lg brut bg-[#0A0A0A] text-white font-bold" data-testid="comment-submit">
                            Post
                        </button>
                    </form>
                ) : (
                    <div className="mb-6 text-sm font-medium text-[#4A4A4A]">
                        <Link to="/login" className="underline font-bold">Log in</Link> to comment.
                    </div>
                )}

                <div className="space-y-3">
                    {comments.length === 0 ? (
                        <div className="text-sm text-[#4A4A4A] font-medium italic">Be the first to comment.</div>
                    ) : (
                        comments.map((c) => (
                            <div key={c.id} className="bg-white brut-inset rounded-lg px-4 py-3" data-testid={`comment-${c.id}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <Link to={`/u/${c.username}`} className="font-bold text-sm hover:underline">@{c.username}</Link>
                                    <span className="text-xs text-[#4A4A4A]">{new Date(c.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="text-sm font-medium">{c.text}</div>
                            </div>
                        ))
                    )}
                </div>
            </section>
        </main>
    );
}
